import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import ActionConfirmCard from "./assistant/ActionConfirmCard";
import VoiceModeOverlay from "./assistant/VoiceModeOverlay";
import { formatDuration, formatUsd } from "./assistant/voiceCost";
import useRealtimeVoice from "../hooks/useRealtimeVoice";
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  Bot,
  User,
  Sparkles,
  Maximize2,
  Minimize2,
  ChevronRight,
  Package,
  Receipt,
  BedDouble,
  LifeBuoy,
  Lightbulb,
  AudioLines,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WELCOME_MESSAGE = {
  role: "assistant",
  content:
    "Ciao! Sono l'assistente virtuale Affidya. Posso spiegarti come usare il gestionale, rispondere alle tue domande e — con la tua conferma — eseguire alcune operazioni come cercare ospiti, consultare i posti liberi o gestire il Deblistering. Come posso aiutarti?",
};

const FALLBACK_QUICK_ACTIONS = [
  {
    id: "cerca_ospite",
    label: "Cerca un ospite",
    message: "Cerca un ospite per nome e mostrami i risultati.",
    icon: "user",
  },
  {
    id: "riepilogo_deblistering",
    label: "Riepilogo Deblistering",
    message: "Mostrami il riepilogo Deblistering: ospiti attivi, fatture da assegnare e da inserire.",
    icon: "package",
  },
  {
    id: "panoramica_ricette",
    label: "Panoramica Ricette",
    message: "Dammi la panoramica Ricette con KPI, pratiche incomplete e anomalie.",
    icon: "sparkles",
  },
  {
    id: "fatture_non_pagate",
    label: "Fatture non pagate",
    message: "Elenca le fatture non pagate o scadute della struttura.",
    icon: "receipt",
  },
  {
    id: "posti_liberi",
    label: "Posti liberi",
    message: "Mostrami il riepilogo dei posti letto liberi per camera.",
    icon: "bed",
  },
  {
    id: "ticket_assistenza",
    label: "Apri ticket assistenza",
    message: "Vorrei aprire un ticket di assistenza verso Affidya per un problema tecnico.",
    icon: "life-buoy",
  },
];

const SUGGESTION_HINTS = [
  "Come genero una fattura?",
  "Quante fatture farmacia devo assegnare in Deblistering?",
  "Mostrami la panoramica Ricette",
  "Spiegami questa pagina",
];

const ACTION_ICONS = {
  user: User,
  package: Package,
  sparkles: Sparkles,
  receipt: Receipt,
  bed: BedDouble,
  "life-buoy": LifeBuoy,
};

function isMarkdownTableRow(line) {
  const t = (line || "").trim();
  return t.startsWith("|") && t.endsWith("|") && t.length > 2;
}

function isMarkdownTableSeparator(line) {
  const t = (line || "").trim();
  if (!isMarkdownTableRow(t)) return false;
  return /^[\|\s:\-]+$/.test(t);
}

function parseMarkdownTableCells(line) {
  return line
    .trim()
    .slice(1, -1)
    .split("|")
    .map((cell) => cell.trim());
}

function renderInlineMarkdown(text, keyPrefix = "t") {
  if (!text) return null;
  const parts = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <strong key={`${keyPrefix}-b-${key++}`} className="font-semibold">
        {match[1]}
      </strong>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length === 1 && typeof parts[0] === "string" ? parts[0] : parts;
}

function MarkdownTable({ lines, tableKey }) {
  if (!lines?.length) return null;
  const headers = parseMarkdownTableCells(lines[0]);
  const rows = lines.slice(2).map(parseMarkdownTableCells);

  return (
    <div key={tableKey} className="overflow-x-auto my-2 rounded-lg border border-slate-200 bg-white shadow-sm">
      <table className="w-full text-xs border-collapse min-w-[260px]">
        <thead>
          <tr className="bg-slate-200/90">
            {headers.map((header, i) => (
              <th
                key={`${tableKey}-h-${i}`}
                className="border-b border-slate-300 px-2.5 py-2 text-left font-semibold text-slate-700 whitespace-nowrap"
              >
                {renderInlineMarkdown(header, `${tableKey}-h-${i}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={`${tableKey}-r-${rowIndex}`}
              className={rowIndex % 2 === 0 ? "bg-white" : "bg-slate-50/90"}
            >
              {headers.map((_, colIndex) => (
                <td
                  key={`${tableKey}-c-${rowIndex}-${colIndex}`}
                  className="border-b border-slate-100 px-2.5 py-2 text-slate-700 align-top"
                >
                  {renderInlineMarkdown(row[colIndex] ?? "", `${tableKey}-c-${rowIndex}-${colIndex}`)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function splitAssistantSegments(content) {
  if (!content) return [];
  const lines = content.split("\n");
  const segments = [];
  let textBuffer = [];
  let index = 0;

  const flushText = () => {
    if (textBuffer.length === 0) return;
    segments.push({ type: "text", content: textBuffer.join("\n") });
    textBuffer = [];
  };

  while (index < lines.length) {
    const line = lines[index];
    const next = lines[index + 1];
    if (isMarkdownTableRow(line) && isMarkdownTableSeparator(next)) {
      flushText();
      const tableLines = [line, next];
      index += 2;
      while (index < lines.length && isMarkdownTableRow(lines[index])) {
        tableLines.push(lines[index]);
        index += 1;
      }
      segments.push({ type: "table", lines: tableLines });
      continue;
    }
    textBuffer.push(line);
    index += 1;
  }

  flushText();
  return segments;
}

function renderAssistantContent(content) {
  if (!content) return null;
  const segments = splitAssistantSegments(content);

  return (
    <div className="space-y-1">
      {segments.map((segment, idx) => {
        if (segment.type === "table") {
          return <MarkdownTable key={`table-${idx}`} lines={segment.lines} tableKey={`table-${idx}`} />;
        }
        const paragraphs = segment.content.split("\n");
        return paragraphs.map((para, pIdx) => {
          if (!para.trim() && pIdx < paragraphs.length - 1) {
            return <div key={`sp-${idx}-${pIdx}`} className="h-2" />;
          }
          return (
            <p key={`p-${idx}-${pIdx}`} className="whitespace-pre-wrap leading-relaxed">
              {renderInlineMarkdown(para, `p-${idx}-${pIdx}`)}
            </p>
          );
        });
      })}
    </div>
  );
}

function ActionIcon({ name, className = "w-4 h-4" }) {
  const Icon = ACTION_ICONS[name] || Sparkles;
  return <Icon className={className} />;
}

function ChatActionButton({ action, onClick, disabled, compact = false }) {
  const isPrimary = action.variant === "primary";
  return (
    <button
      type="button"
      onClick={() => onClick(action.message)}
      disabled={disabled}
      className={
        compact
          ? `inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
              isPrimary
                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            }`
          : `w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors disabled:opacity-50 ${
              isPrimary
                ? "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-emerald-200"
            }`
      }
    >
      {action.icon && !compact && (
        <span className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
          isPrimary ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"
        }`}>
          <ActionIcon name={action.icon} />
        </span>
      )}
      <span className="flex-1 min-w-0">{action.label}</span>
      {!compact && <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />}
    </button>
  );
}

export default function AssistantChatWidget() {
  const { token, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [available, setAvailable] = useState(null);
  const [quickActions, setQuickActions] = useState(FALLBACK_QUICK_ACTIONS);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionStates, setActionStates] = useState({});
  const [voiceStatus, setVoiceStatus] = useState({ visible: false, available: false, message: null });
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voiceMinimized, setVoiceMinimized] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const checkStatusRef = useRef(null);

  const handleVoiceEnd = useCallback((summary) => {
    setVoiceOpen(false);
    setVoiceMinimized(false);
    const parts = ["EVA · conversazione terminata", formatDuration(summary?.duration_seconds)];
    if (summary?.show_cost && typeof summary.session_cost_usd === "number") {
      parts.push(formatUsd(summary.session_cost_usd));
    }
    const executed = Number(summary?.actions_executed) || 0;
    if (executed > 0) parts.push(executed === 1 ? "1 azione eseguita" : `${executed} azioni eseguite`);
    setMessages((prev) => [...prev, { role: "voice_summary", content: parts.join(" · ") }]);
    if (summary?.notice) toast.info(summary.notice);
    checkStatusRef.current?.();
  }, []);

  const voice = useRealtimeVoice({ token, onSessionEnd: handleVoiceEnd });

  const startVoice = () => {
    if (!voiceStatus.available) {
      if (voiceStatus.message) toast.info(voiceStatus.message);
      return;
    }
    setOpen(true);
    setExpanded(true);
    setVoiceMinimized(false);
    setVoiceOpen(true);
    voice.start();
  };

  const closeChat = () => {
    if (voiceOpen) {
      voice.cancelStart();
      setVoiceOpen(false);
      setVoiceMinimized(false);
    }
    setOpen(false);
  };

  const setActionState = useCallback((id, next) => {
    if (!id) return;
    setActionStates((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...next } }));
  }, []);

  const applyResolvedActions = useCallback((resolved) => {
    (resolved || []).forEach((r) => {
      if (r?.id) setActionState(r.id, { status: r.outcome, message: r.message });
    });
  }, [setActionState]);

  const checkStatus = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/api/assistant/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailable(res.data?.available === true);
      if (Array.isArray(res.data?.quick_actions) && res.data.quick_actions.length > 0) {
        setQuickActions(res.data.quick_actions);
      }
      setVoiceStatus({
        visible: res.data?.voice_visible === true,
        available: res.data?.voice_available === true,
        message: res.data?.voice_block_message || null,
      });
    } catch {
      setAvailable(false);
    }
  }, [token]);

  useEffect(() => {
    checkStatusRef.current = checkStatus;
    checkStatus();
  }, [checkStatus]);

  useEffect(() => {
    if (!open || !scrollRef.current) return;
    const isWelcomeOnly =
      messages.length === 1
      && messages[0].role === "assistant"
      && messages[0].content === WELCOME_MESSAGE.content;
    scrollRef.current.scrollTop = isWelcomeOnly ? 0 : scrollRef.current.scrollHeight;
  }, [messages, open, loading, expanded]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setExpanded(true);
    }
  }, [open]);

  const openChat = () => {
    setExpanded(true);
    setOpen(true);
  };


  const sendMessage = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;

    const userMessage = { role: "user", content: text };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInput("");
    setLoading(true);

    try {
      const payload = nextMessages
        .filter((m) => m.role === "user" || m.role === "assistant")
        .map((m) => ({ role: m.role, content: m.content }));

      const res = await axios.post(
        `${API_URL}/api/assistant/chat`,
        { messages: payload },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const reply = res.data?.message || "Nessuna risposta ricevuta.";
      const actions = res.data?.suggested_actions || [];
      const pendingActions = res.data?.pending_actions || [];
      applyResolvedActions(res.data?.resolved_actions);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: reply, actions, pendingActions },
      ]);

      if (res.data?.disabled) {
        setAvailable(false);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : "Errore di comunicazione con l'assistente. Riprova.";
      toast.error(msg);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "Mi dispiace, non sono riuscito a rispondere. Puoi riprovare o usare la pagina Assistenza dal menu utente.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const resolveActionByButton = async (action, kind) => {
    if (!action?.id) return;
    setActionState(action.id, { status: kind === "confirm" ? "confirming" : "cancelling" });
    try {
      const res = await axios.post(
        `${API_URL}/api/assistant/actions/${action.id}/${kind}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = res.data || {};
      let status = "failed";
      if (data.cancelled) status = "cancelled";
      else if (data.success) status = "executed";
      setActionState(action.id, { status, message: data.message || data.error });
      if (data.message) {
        setMessages((prev) => [...prev, { role: "assistant", content: data.message }]);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Operazione non riuscita. Riprova.");
      setActionState(action.id, { status: "pending" });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const showWelcomeExtras =
    messages.length === 1
    && messages[0].role === "assistant"
    && messages[0].content === WELCOME_MESSAGE.content;
  const firstName = (user?.name || "").split(" ")[0] || "utente";

  if (available === false) {
    return null;
  }

  const panelClassName = expanded
    ? "fixed inset-y-0 right-0 z-[200] w-[min(100vw,540px)] min-h-0 flex flex-col overflow-hidden border-l border-slate-200 bg-white shadow-[-8px_0_32px_rgba(15,23,42,0.12)]"
    : "fixed bottom-20 right-4 z-[100] w-[min(100vw-2rem,400px)] h-[min(70vh,520px)] flex flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden";

  return (
    <>
      {open && (
        <div className={panelClassName} data-testid="assistant-chat-panel">
          {voiceOpen && !voiceMinimized ? (
            <VoiceModeOverlay
              voice={voice}
              panelExpanded={expanded}
              onMinimize={() => setVoiceMinimized(true)}
              onClose={() => {
                setVoiceOpen(false);
                setVoiceMinimized(false);
              }}
              onClosePanel={closeChat}
              onTogglePanel={() => setExpanded((v) => !v)}
            />
          ) : (
          <>
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">Assistente Affidya</p>
                <p className="text-[10px] text-white/80 truncate">Intelligenza artificiale</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {voiceStatus.visible && !voiceOpen && (
                <button
                  type="button"
                  onClick={startVoice}
                  className={`p-1.5 rounded-lg transition-colors ${voiceStatus.available ? "hover:bg-white/20" : "opacity-50 cursor-not-allowed"}`}
                  aria-label="Parla con EVA"
                  title={voiceStatus.available ? "Parla con EVA" : voiceStatus.message || "EVA non è disponibile"}
                  data-testid="assistant-voice-header"
                >
                  <AudioLines className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                aria-label={expanded ? "Riduci pannello" : "Ingrandisci pannello"}
                data-testid="assistant-chat-expand"
              >
                {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <button
                type="button"
                onClick={closeChat}
                className="p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                aria-label="Chiudi assistente"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3 min-h-0" ref={scrollRef}>
            <div className="space-y-3 pr-1">
              {showWelcomeExtras && (
                <div className="space-y-4 pb-2">
                  <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-2.5">
                    <p className="text-sm text-emerald-900">
                      Ciao <span className="font-semibold">{firstName}</span>! 👋
                    </p>
                    <p className="text-xs text-emerald-800/80 mt-1 leading-relaxed">
                      Chiedimi come usare Affidya o usa un&apos;azione rapida per consultare dati ed eseguire operazioni.
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 px-1">
                      Azioni rapide
                    </p>
                    <div className={`grid gap-2 ${expanded ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
                      {quickActions.map((action) => (
                        <ChatActionButton
                          key={action.id}
                          action={action}
                          onClick={sendMessage}
                          disabled={loading || available === null}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 mb-2">
                      <Lightbulb className="w-3.5 h-3.5 text-amber-500" />
                      Suggerimenti
                    </div>
                    <ul className="space-y-1.5">
                      {SUGGESTION_HINTS.map((hint) => (
                        <li key={hint}>
                          <button
                            type="button"
                            onClick={() => sendMessage(hint)}
                            disabled={loading || available === null}
                            className="text-xs text-emerald-700 hover:text-emerald-800 hover:underline text-left disabled:opacity-50"
                          >
                            {hint}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {messages.map((msg, idx) => (msg.role === "voice_summary" ? (
                <div key={`${msg.role}-${idx}`} className="flex justify-center">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-xs text-slate-600">
                    <AudioLines className="w-3.5 h-3.5 text-emerald-600" />
                    {msg.content}
                  </span>
                </div>
              ) : (
                <div key={`${msg.role}-${idx}`}>
                  <div className={`flex gap-2 ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                        msg.role === "user"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {msg.role === "user" ? (
                        <User className="w-3.5 h-3.5" />
                      ) : (
                        <Bot className="w-3.5 h-3.5" />
                      )}
                    </div>
                    <div
                      className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                        msg.role === "user"
                          ? "bg-emerald-600 text-white rounded-tr-sm whitespace-pre-wrap"
                          : "bg-slate-100 text-slate-800 rounded-tl-sm"
                      }`}
                    >
                      {msg.role === "assistant" ? renderAssistantContent(msg.content) : msg.content}
                    </div>
                  </div>

                  {msg.role === "assistant" && msg.pendingActions?.length > 0 && (
                    <div className="mt-2 ml-9 space-y-2 max-w-[85%]">
                      {msg.pendingActions.map((action) => (
                        <ActionConfirmCard
                          key={action.id}
                          action={action}
                          status={actionStates[action.id]?.status || "pending"}
                          onConfirm={(a) => resolveActionByButton(a, "confirm")}
                          onCancel={(a) => resolveActionByButton(a, "cancel")}
                        />
                      ))}
                    </div>
                  )}

                  {msg.role === "assistant" && msg.actions?.length > 0 && (
                    <div className="mt-2 ml-9 flex flex-wrap gap-2">
                      {msg.actions.map((action) => (
                        <ChatActionButton
                          key={action.id || action.label}
                          action={action}
                          onClick={sendMessage}
                          disabled={loading}
                          compact
                        />
                      ))}
                    </div>
                  )}
                </div>
              )))}

              {loading && (
                <div className="flex gap-2 items-center text-emerald-700 text-sm pl-9">
                  <span className="relative flex h-4 w-4 flex-shrink-0" aria-hidden="true">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400/70 animate-ping motion-reduce:animate-none" />
                    <span className="relative inline-flex h-4 w-4 rounded-full bg-emerald-500" />
                  </span>
                  Sto elaborando…
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-slate-100 p-3 bg-slate-50 flex-shrink-0">
            {voiceOpen && voiceMinimized && (
              <div className="mb-3">
                <VoiceModeOverlay
                  voice={voice}
                  minimized
                  onExpand={() => {
                    setExpanded(true);
                    setVoiceMinimized(false);
                  }}
                  onClose={() => {
                    setVoiceOpen(false);
                    setVoiceMinimized(false);
                  }}
                />
              </div>
            )}
            {!expanded && (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="w-full mb-2 text-xs text-emerald-700 hover:text-emerald-800 font-medium flex items-center justify-center gap-1.5 py-1"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Apri pannello esteso
              </button>
            )}
            <div className="flex gap-2 items-end">
              <Textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Scrivi la tua domanda..."
                disabled={loading || available === null}
                className="min-h-[44px] max-h-28 resize-none text-sm bg-white"
                rows={1}
                data-testid="assistant-chat-input"
              />
              {voiceStatus.visible && (
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={startVoice}
                  disabled={voiceOpen || available === null}
                  className={`h-10 w-10 flex-shrink-0 border-emerald-200 text-emerald-700 hover:bg-emerald-50 ${
                    voiceStatus.available ? "" : "opacity-50"
                  }`}
                  aria-label="Parla con EVA"
                  title={voiceStatus.available ? "Parla con EVA" : voiceStatus.message || "EVA non è disponibile"}
                  data-testid="assistant-voice-start"
                >
                  <AudioLines className="w-4 h-4" />
                </Button>
              )}
              <Button
                type="button"
                size="icon"
                onClick={() => sendMessage()}
                disabled={loading || !input.trim() || available === null}
                className="h-10 w-10 flex-shrink-0 bg-emerald-600 hover:bg-emerald-700"
                data-testid="assistant-chat-send"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5 text-center">
              Le azioni importanti richiedono la tua conferma esplicita.
            </p>
          </div>
          </>
          )}
        </div>
      )}

      {(!open || !expanded) && (
        <button
          type="button"
          onClick={() => (open ? closeChat() : openChat())}
          className={`fixed right-0 bottom-[35px] z-[100] flex flex-col items-center gap-2 rounded-l-xl rounded-r-none px-2.5 py-3.5 shadow-lg transition-all ${
            open
              ? "bg-slate-700 hover:bg-slate-800 text-white"
              : "bg-emerald-600 hover:bg-emerald-700 text-white hover:pl-3.5"
          }`}
          aria-label={open ? "Chiudi assistente" : "Apri assistente virtuale"}
          data-testid="assistant-chat-toggle"
        >
          {open ? <X className="h-5 w-5 shrink-0" /> : <MessageCircle className="h-5 w-5 shrink-0" />}
          <span className="select-none text-[11px] font-semibold uppercase tracking-[0.18em] [writing-mode:vertical-rl] rotate-180">
            Assistente
          </span>
        </button>
      )}

    </>
  );
}
